import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Card, CardContent, IconButton, useMediaQuery, useTheme, Button } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate } from 'react-router-dom';
import Axios from "axios";
import MapComponent from '../Map/MapComponent';
import { categories as geoCategories, admlayers as geoAdmLayers } from '../../Constants/GeologicalOptions';
import StateContext from '../../Contexts/StateContext';
import DispatchContext from '../../Contexts/DispatchContext';
import DesktopFilter from './DesktopFilter';
import MobileDrawer from './MobileDrawer';
import config, { environment } from '../../config';

function Feltarasok() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const appState = useContext(StateContext);
  const appDispatch = useContext(DispatchContext);

  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(['Foldtaniobjektum']);
  const [selectedAdmLayers, setSelectedAdmLayers] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState({
    'Foldtaniobjektum': {
      'Kofejto': true,
      'Banya': true,
      'Bezartbanya': true,
      'Olajesgaz': true,
      'Kilato': true,
      'Iszapvulkan': true,
    },
    'Asvanyviz': {
      'Borviz': true,
      'Forras': true,
      'Mofetta': true,
      'Radioaktiv': true,
      'Kenes': true,
    }
  });

  // Data fetching effects
  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getGeologicalObjects() {
      if (selectedCategories.includes('Foldtaniobjektum')) {
        try {
          const endpoint = config[environment].geologicalEndpoint;
          const response = await Axios.get(endpoint, { 
            cancelToken: source.token,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          appDispatch({ type: 'setGeologicalObjects', data: response.data });
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching geological objects:", error);
        }
      }
    }
    getGeologicalObjects();
    return () => source.cancel();
  }, [selectedCategories, appDispatch]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getMineralWaters() {
      if (selectedCategories.includes('Asvanyviz')) {
        try {
          const endpoint = config[environment].mineralWaterEndpoint;
          const response = await Axios.get(endpoint, { 
            cancelToken: source.token,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          appDispatch({ type: 'setMineralWaters', data: response.data });
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching mineral waters:", error);
        }
      }
    }
    getMineralWaters();
    return () => source.cancel();
  }, [selectedCategories, appDispatch]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getParishes(page = 1) {
      if (selectedAdmLayers.includes('kozsegvaros')) {
        try {
          const endpoint = config[environment].parishEndpoint(page);
          const response = await Axios.get(endpoint, { 
            cancelToken: source.token,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          appDispatch({ 
            type: page === 1 ? 'setParishes' : 'appendParishes', 
            data: response.data.results 
          });
          if (response.data.next) {
            // Give a small timeout to avoid hitting rate limits or overwhelming the reducer
            setTimeout(() => getParishes(page + 1), 100);
          }
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching parishes:", error);
        }
      } else {
        if (page === 1) appDispatch({ type: 'setParishes', data: [] });
      }
    }
    getParishes();
    return () => source.cancel();
  }, [selectedAdmLayers.includes('kozsegvaros'), appDispatch]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getTelepulesek(page = 1) {
      if (selectedAdmLayers.includes('telepules')) {
        try {
          const endpoint = config[environment].telepulesEndpoint(page);
          const response = await Axios.get(endpoint, { 
            cancelToken: source.token,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          appDispatch({ 
            type: page === 1 ? 'setTelepulesek' : 'appendTelepulesek', 
            data: response.data.results 
          });
          if (response.data.next) {
            setTimeout(() => getTelepulesek(page + 1), 100);
          }
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching telepulesek:", error);
        }
      } else {
        // Only clear if the layer is not being loaded
        if (page === 1) appDispatch({ type: 'setTelepulesek', data: [] });
      }
    }
    getTelepulesek();
    return () => source.cancel();
  }, [selectedAdmLayers.includes('telepules'), appDispatch]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getSzekek(page = 1) {
      if (selectedAdmLayers.includes('szek')) {
        try {
          const endpoint = config[environment].szekEndpoint(page);
          const response = await Axios.get(endpoint, { 
            cancelToken: source.token,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          appDispatch({ 
            type: page === 1 ? 'setSzekek' : 'appendSzekek', 
            data: response.data.results 
          });
          if (response.data.next) {
            setTimeout(() => getSzekek(page + 1), 100);
          }
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching szekek:", error);
        }
      } else {
        if (page === 1) appDispatch({ type: 'setSzekek', data: [] });
      }
    }
    getSzekek();
    return () => source.cancel();
  }, [selectedAdmLayers.includes('szek'), appDispatch]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getMegyek(page = 1) {
      if (selectedAdmLayers.includes('megye')) {
        try {
          const endpoint = config[environment].megyeEndpoint(page);
          const response = await Axios.get(endpoint, { 
            cancelToken: source.token,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          appDispatch({ 
            type: page === 1 ? 'setMegyek' : 'appendMegyek', 
            data: response.data.results 
          });
          if (response.data.next) {
            setTimeout(() => getMegyek(page + 1), 100);
          }
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching megyek:", error);
        }
      } else {
        if (page === 1) appDispatch({ type: 'setMegyek', data: [] });
      }
    }
    getMegyek();
    return () => source.cancel();
  }, [selectedAdmLayers.includes('megye'), appDispatch]);

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function getHelynevek(page = 1) {
      if (selectedAdmLayers.includes('helynev')) {
        try {
          const endpoint = config[environment].helynevEndpoint(page);
          const response = await Axios.get(endpoint, { 
            cancelToken: source.token,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          });
          appDispatch({ 
            type: page === 1 ? 'setHelynevek' : 'appendHelynevek', 
            data: response.data.results 
          });
          if (response.data.next) {
            setTimeout(() => getHelynevek(page + 1), 100);
          }
        } catch (error) {
          if (!Axios.isCancel(error)) console.error("Error fetching helynevek:", error);
        }
      } else {
        if (page === 1) appDispatch({ type: 'setHelynevek', data: [] });
      }
    }
    getHelynevek();
    return () => source.cancel();
  }, [selectedAdmLayers.includes('helynev'), appDispatch]);

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
    );
  };

  const handleKeyToggle = (category) => (key) => {
    setSelectedKeys(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: !prev[category][key]
      }
    }));
  };

  useEffect(() => {
    // When selectedKeys change, we might want to trigger a refresh or just rely on the filteredGeologicalObjects recalculation
  }, [selectedKeys]);

  const handleAdmLayerToggle = (layer) => {
    setSelectedAdmLayers(prev => 
      prev.includes(layer) ? prev.filter(l => l !== layer) : [...prev, layer]
    );
  };

  // Filter objects based on selection
  const filteredGeologicalObjects = {};
  Object.keys(appState.allGeologicalObjects).forEach(category => {
    if (selectedCategories.includes(category)) {
      filteredGeologicalObjects[category] = appState.allGeologicalObjects[category].filter(obj => {
          const categoryKeys = selectedKeys[category];
          if (categoryKeys) {
            const objType = obj.geological_type || obj.type || obj.listing_type || obj.type_name;
            // Map model names to keys if necessary, but here they seem to match
            if (objType && categoryKeys[objType] !== undefined) {
                return categoryKeys[objType];
            }
          }
          return true; 
      });
    }
  });

  const filteredMineralWaters = {};
  Object.keys(appState.allMineralWaters).forEach(category => {
    if (selectedCategories.includes('Asvanyviz')) {
      filteredMineralWaters[category] = appState.allMineralWaters[category].filter(obj => {
          const categoryKeys = selectedKeys['Asvanyviz'];
          if (categoryKeys) {
            if (categoryKeys[category] !== undefined) {
                return categoryKeys[category];
            }
          }
          return true;
      });
    }
  });

  return (
    <Box sx={{ 
      height: 'calc(100vh - 64px)', // Adjust based on Header height
      width: '100%', 
      display: 'flex', 
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Desktop Sidebar (Filter) */}
      {!isMobile && (
        <Box sx={{ 
          width: 300, 
          height: '100%', 
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
          backgroundColor: 'background.paper',
          zIndex: 10
        }}>
          <DesktopFilter 
            categories={geoCategories}
            selectedCategories={selectedCategories}
            selectedKeys={selectedKeys}
            admlayers={geoAdmLayers}
            selectedAdmLayers={selectedAdmLayers}
            handleCategoryToggle={handleCategoryToggle}
            handleKeyToggle={handleKeyToggle}
            handleAdmLayerToggle={handleAdmLayerToggle}
          />
        </Box>
      )}

      {/* Main Map Area */}
      <Box sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
        <MapComponent 
          allGeologicalObjects={filteredGeologicalObjects}
          allMineralWaters={filteredMineralWaters}
          allParishes={selectedAdmLayers.includes('kozsegvaros') ? appState.allParishes : []}
          allTelepulesek={selectedAdmLayers.includes('telepules') ? appState.allTelepulesek : []}
          allSzekek={selectedAdmLayers.includes('szek') ? appState.allSzekek : []}
          allMegyek={selectedAdmLayers.includes('megye') ? appState.allMegyek : []}
          allHelynevek={selectedAdmLayers.includes('helynev') ? appState.allHelynevek : []}
          navigate={navigate}
          dispatch={appDispatch}
          forcedMaxZoom={10}
        />

        {/* Mobile Filter Button */}
        {isMobile && (
          <Button 
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterOpen(true)}
            sx={{ 
              position: 'absolute', 
              bottom: 20, 
              left: '50%', 
              transform: 'translateX(-50%)', 
              zIndex: 1000,
              borderRadius: 20,
              px: 3
            }}
          >
            Szűrők
          </Button>
        )}
      </Box>

      {/* Mobile Drawer */}
      <MobileDrawer 
        filterOpen={filterOpen}
        setFilterOpen={setFilterOpen}
        selectedCategories={selectedCategories}
        selectedKeys={selectedKeys}
        selectedAdmLayers={selectedAdmLayers}
        handleCategoryToggle={handleCategoryToggle}
        handleKeyToggle={handleKeyToggle}
        handleAdmLayerToggle={handleAdmLayerToggle}
      />
    </Box>
  );
}

export default Feltarasok;
